/**
 * integrationConfigs — the per-integration content registry that drives the
 * shared <IntegrationLandingPage> template. One entry per public integration
 * marketing page (/hermes, /cursor, /claude-code, /codex).
 *
 * VERIFIED install facts (no fabrication):
 *  - SDK: `pip install cellcog`; `from cellcog import CellCogClient`;
 *    CellCogClient(agent_provider="..."); CELLCOG_API_KEY (sk_...).
 *    Source: CellCog OpenClaw Guide + docs/integrations/openclaw_system_design.md
 *  - Plugin (Open Plugins standard): repo github.com/CellCog/cellcog-plugin,
 *    16 curated skills. Per-tool install per docs/integrations/plugin_design.md.
 *
 * SEO/AEO: each config carries `keywords` (per-platform SoftwareApplication
 * schema) and `faqs` (rendered on-page + shipped as FAQPage structured data —
 * answers written answer-first so answer engines can lift them verbatim).
 *
 * To add a future integration: append a config here and a route in App.js.
 */

import {
  MCP_SERVER_URL, MCP_CLAUDE_CODE_COMMAND, MCP_CODEX_COMMAND, MCP_CODEX_LOGIN_COMMAND,
} from '../../config/mcpServerConfig';

const PLUGIN_REPO = 'https://github.com/CellCog/cellcog-plugin';

// Shared "what you get" value props (common across integrations).
const COMMON_VALUE_PROPS = [
  {
    title: 'Every modality, one key',
    body: 'Research, video, images, audio, music, PDFs, decks, apps, dashboards, 3D — all from a single CellCog key.',
  },
  {
    title: 'Finished artifacts, not snippets',
    body: 'Delegate a task and get back the completed deliverable — the same output a human would receive.',
  },
  {
    title: 'Deep reasoning underneath',
    body: 'Built on CellCog, a leader on DeepResearch Bench — the reasoning that powers every output.',
  },
];

// Shared pricing FAQ (identical honest answer on every integration page).
const FAQ_PRICING = {
  q: 'How much does CellCog cost for agents?',
  a: 'Try it free, no credit card needed. Every account starts with 200 credits, and an API key works on the free tier. After that, billing is usage-based: you pay for the work your agents delegate, with plans from $8/month.',
};

// Subagents section (Nitish, Oct 12 2026): the other way round, a CellCog chat or AI employee starting this tool
// on the user's own computer under their own plan. Facts: CellCog Subagents Guide.
const subagentsFor = (name, plan) => ({
  title: `Your AI employee can hand work to ${name}.`,
  body: `A CellCog chat or AI employee can start ${name} as a subagent on your own computer, signed in with the ${plan} subscription you already pay for, not CellCog credits. It steps away while ${name} works, then reads the result and marks it reviewed before calling the job done.`,
});

export const INTEGRATION_CONFIGS = {
  hermes: {
    slug: 'hermes',
    name: 'Hermes',
    logo: '/assets/icons/agents/hermes-light.png?v=7',
    logoLight: '/assets/icons/agents/hermes-dark.png', // adaptive light face (dark-artwork twin)
    headline: 'CellCog for Hermes',
    headlineAccent: 'every capability, one SDK.',
    tagline:
      'Bring CellCog’s full platform to your Hermes agents through the CellCog Python SDK — research, every modality, and finished artifacts, delegated in one call.',
    installKind: 'sdk',
    providerValue: 'hermes',
    valueProps: COMMON_VALUE_PROPS,
    ctaLabel: 'Get your API key',
    seo: {
      title: 'CellCog for Hermes — Every Capability via One SDK | CellCog',
      description:
        'Connect CellCog to your Hermes agents with the CellCog Python SDK. Delegate research, video, PDFs, apps, and dashboards — a leader on DeepResearch Bench — with one API key.',
    },
    keywords: 'Hermes agent SDK, CellCog Python SDK, API for AI agents, Hermes agent integration, delegate tasks from Hermes agents',
    faqs: [
      {
        q: 'How do Hermes agents connect to CellCog?',
        a: 'Hermes agents connect through the CellCog Python SDK: pip install cellcog, set your CELLCOG_API_KEY, and create the client with agent_provider="hermes". One API key exposes the full CellCog platform, and finished work is returned to your agent on completion.',
      },
      {
        q: 'What can a Hermes agent delegate to CellCog?',
        a: 'A Hermes agent can delegate deep research, documents, PDFs, images, video, apps, and dashboards to CellCog. The agent sends one prompt and receives the completed artifact — the same output a human CellCog user would get.',
      },
      FAQ_PRICING,
    ],
  },
  cursor: {
    slug: 'cursor',
    name: 'Cursor',
    // IntegrationLandingPage is a DARK-ONLY shell, so the mark must be light-inked:
    // cursor-dark.svg = light fill #edecec (for dark bg); cursor-light.svg = dark fill #26251e.
    logo: '/assets/icons/agents/cursor-dark.svg',
    logoLight: '/assets/icons/agents/cursor-light.svg', // adaptive light face (dark-artwork twin)
    headline: 'CellCog for Cursor',
    subagents: subagentsFor('Cursor', 'Cursor'),
    headlineAccent: 'beyond code, inside your editor.',
    tagline:
      'Install the CellCog plugin and /cellcog shows up in Cursor — generate research, media, documents, and dashboards without leaving your editor.',
    installKind: 'plugin',
    installCommand: '/add-plugin cellcog',
    // The second way in (Nitish, Oct 11 2026): MCP, no API key. Same facts as the Delegate app (delegateAgents.js).
    mcp: {
      intro: 'Prefer no API key? In Cursor\u2019s MCP settings, add this address as a remote server:',
      commands: [MCP_SERVER_URL],
      outro: 'The first time Cursor uses it, sign in with your CellCog account and approve.',
    },
    valueProps: COMMON_VALUE_PROPS,
    ctaLabel: 'Get it on cursor.directory',
    ctaHref: 'https://cursor.directory/plugins/cellcog',
    seo: {
      title: 'CellCog for Cursor — Research, Media & Docs in Your Editor | CellCog',
      description:
        'Add the CellCog plugin to Cursor for research, images, video, PDFs, and dashboards — a leader on DeepResearch Bench. Install from cursor.directory.',
    },
    keywords: 'Cursor plugin, CellCog Cursor plugin, AI research plugin for Cursor, generate documents in Cursor, cursor.directory plugins',
    faqs: [
      {
        q: 'How do I install the CellCog plugin in Cursor?',
        a: 'Install CellCog in Cursor with one command: /add-plugin cellcog, or get it from cursor.directory. Add your CellCog API key and /cellcog becomes available inside your editor.',
      },
      {
        q: 'What can CellCog do inside Cursor?',
        a: 'CellCog lets Cursor go beyond code: generate deep research, images, video, PDFs, documents, and dashboards without leaving your editor. Your coding agent delegates the task and the finished file comes back to you.',
      },
      FAQ_PRICING,
    ],
  },
  'claude-code': {
    slug: 'claude-code',
    name: 'Claude Code',
    logo: '/assets/icons/agents/claude.png',
    headline: 'CellCog for Claude Code',
    subagents: subagentsFor('Claude Code', 'Claude'),
    headlineAccent: 'every modality, one plugin.',
    tagline:
      'CellCog installs as an Open Plugins plugin in Claude Code — your coding agent can generate research, media, and documents without leaving the terminal.',
    installKind: 'plugin',
    installCommand: 'claude --plugin-dir /path/to/cellcog-plugin',
    mcp: {
      intro: 'Prefer no API key? Connect over MCP instead:',
      commands: [MCP_CLAUDE_CODE_COMMAND],
      outro: 'The first time you use it, your browser opens a CellCog page. Approve once.',
    },
    valueProps: COMMON_VALUE_PROPS,
    ctaLabel: 'Get the plugin',
    ctaHref: PLUGIN_REPO,
    seo: {
      title: 'CellCog for Claude Code — Research, Media & Docs Plugin | CellCog',
      description:
        'Add CellCog to Claude Code as an Open Plugins plugin. Generate research, video, PDFs, apps, and dashboards — a leader on DeepResearch Bench — from your coding agent.',
    },
    keywords: 'Claude Code plugin, CellCog plugin, Open Plugins, AI research plugin for Claude Code, generate media from Claude Code',
    faqs: [
      {
        q: 'How do I add CellCog to Claude Code?',
        a: 'CellCog installs in Claude Code as an Open Plugins plugin: get the cellcog-plugin repo, run claude --plugin-dir with its path, and add your CellCog API key. Your coding agent can then call every CellCog capability from the terminal.',
      },
      {
        q: 'What can Claude Code generate through CellCog?',
        a: 'Through CellCog, Claude Code can generate deep research reports, images, video, PDFs, documents, and dashboards — work that goes beyond code, delivered back as finished files while you keep coding.',
      },
      FAQ_PRICING,
    ],
  },
  codex: {
    slug: 'codex',
    name: 'Codex',
    logo: '/assets/icons/agents/openai-light.svg',
    logoLight: '/assets/icons/agents/openai-dark.svg', // adaptive light face (dark-artwork twin)
    headline: 'CellCog for Codex',
    subagents: subagentsFor('Codex', 'ChatGPT'),
    headlineAccent: 'every modality, one plugin.',
    tagline:
      'CellCog follows the Open Plugins standard, so Codex can call its full platform — research, media, documents, and dashboards — through the same cellcog-plugin.',
    installKind: 'plugin',
    installCommand: 'Install via the Open Plugins standard (cellcog-plugin repo)',
    mcp: {
      intro: 'Prefer no API key? Connect over MCP instead:',
      commands: [MCP_CODEX_COMMAND, MCP_CODEX_LOGIN_COMMAND],
      outro: 'The second command opens a CellCog page in your browser. Click Approve.',
    },
    valueProps: COMMON_VALUE_PROPS,
    ctaLabel: 'Get the plugin',
    ctaHref: PLUGIN_REPO,
    seo: {
      title: 'CellCog for Codex — Open Plugins Integration | CellCog',
      description:
        'CellCog works with Codex via the Open Plugins standard. Generate research, video, PDFs, apps, and dashboards — a leader on DeepResearch Bench — from one plugin.',
    },
    keywords: 'Codex plugin, Open Plugins standard, CellCog Codex integration, AI agent plugin for Codex',
    faqs: [
      {
        q: 'Does CellCog work with Codex?',
        a: 'Yes. CellCog follows the Open Plugins standard, so Codex integrates through the same cellcog-plugin used by Claude Code and Cursor. Any Open Plugins–conformant agent can call the full CellCog platform.',
      },
      {
        q: 'What can Codex delegate to CellCog?',
        a: 'Codex can delegate deep research, documents, PDFs, images, video, apps, and dashboards to CellCog through the plugin, and receives the finished artifact back — the same output a human CellCog user would get.',
      },
      FAQ_PRICING,
    ],
  },
};

export const PLUGIN_REPO_URL = PLUGIN_REPO;
